import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Percent, Plus, Search, Edit2, Trash2 } from 'lucide-react';
import apiService from '../../services/api';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import Table from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

interface Vat {
  _id: string;
  name: string;
  rate: number;
  description?: string;
  isActive: boolean;
}

const VatsPage: React.FC = () => {
  const [vats, setVats] = useState<Vat[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVat, setEditingVat] = useState<Vat | null>(null);
  const [formData, setFormData] = useState({ name: '', rate: 0, description: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchVats();
  }, []);

  const fetchVats = async () => {
    try {
      setLoading(true);
      const response = await apiService.getVats();
      setVats(response.data.vats || []);
    } catch (error: any) {
      console.error('Error fetching VAT rates:', error);
      toast.error(error.response?.data?.error || 'Failed to fetch VAT rates');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (vat?: Vat) => {
    if (vat) {
      setEditingVat(vat);
      setFormData({ name: vat.name, rate: vat.rate, description: vat.description || '' });
    } else {
      setEditingVat(null);
      setFormData({ name: '', rate: 0, description: '' });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingVat(null);
    setFormData({ name: '', rate: 0, description: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('VAT name is required');
      return;
    }

    try {
      setSubmitting(true);
      if (editingVat) {
        await apiService.updateVat(editingVat._id, formData);
        toast.success('VAT rate updated successfully');
      } else {
        await apiService.createVat(formData);
        toast.success('VAT rate created successfully');
      }
      handleCloseModal();
      fetchVats();
    } catch (error: any) {
      console.error('Error saving VAT rate:', error);
      toast.error(error.response?.data?.error || 'Failed to save VAT rate');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this VAT rate?')) return;

    try {
      await apiService.deleteVat(id);
      toast.success('VAT rate deleted successfully');
      fetchVats();
    } catch (error: any) {
      console.error('Error deleting VAT rate:', error);
      toast.error(error.response?.data?.error || 'Failed to delete VAT rate');
    }
  };

  const filteredVats = vats.filter(vat =>
    vat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { header: 'Name', accessor: (row: Vat) => row.name },
    { header: 'Rate (%)', accessor: (row: Vat) => `${row.rate}%` },
    { header: 'Description', accessor: (row: Vat) => row.description || '-' },
    {
      header: 'Actions',
      accessor: (row: Vat) => (
        <div className="flex gap-0.5">
          <Button variant="ghost" size="sm" className="text-amber-600 hover:text-amber-700 hover:bg-amber-50" icon={<Edit2 className="w-4 h-4" />} onClick={() => handleOpenModal(row)} />
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" icon={<Trash2 className="w-4 h-4" />} onClick={() => handleDelete(row._id)} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="flex justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">VAT Rates</h1>
          <p className="mt-2 text-sm text-gray-600">Manage VAT rate information</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add VAT Rate</Button>
      </motion.div>

      <Card padding="md">
        <Input placeholder="Search VAT rates..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} icon={<Search className="w-5 h-5" />} fullWidth />
      </Card>

      <Card padding="none">
        {loading ? (
          <div className="flex justify-center items-center py-12"><LoadingSpinner size="lg" /></div>
        ) : filteredVats.length > 0 ? (
          <Table data={filteredVats} columns={columns} emptyMessage="No VAT rates found" />
        ) : (
          <div className="px-6 py-12 text-center">
            <Percent className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No VAT rates yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first VAT rate.</p>
            <div className="mt-6"><Button icon={<Plus className="w-4 h-4" />} onClick={() => handleOpenModal()}>Add VAT Rate</Button></div>
          </div>
        )}
      </Card>

      <Modal isOpen={modalOpen} onClose={handleCloseModal} title={editingVat ? 'Edit VAT Rate' : 'Add New VAT Rate'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input label="Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Enter VAT name" required fullWidth />
          <Input label="Rate (%) *" type="number" step="0.01" min="0" max="100" value={formData.rate} onChange={(e) => setFormData({ ...formData, rate: parseFloat(e.target.value) || 0 })} placeholder="Enter rate percentage" required fullWidth />
          <Input label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Enter description (optional)" fullWidth />
          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={submitting}>Cancel</Button>
            <Button type="submit" loading={submitting}>{editingVat ? 'Update VAT Rate' : 'Create VAT Rate'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default VatsPage;

